import React, { useMemo, useState, useEffect } from "react";
import { useSearchParams, useLocation, useNavigate } from "react-router-dom";
import { Search, ChevronDown, Download, Upload, Plus, X, FileX, FileDown } from "lucide-react";
import TopBar from "../components/layout/TopBar";
import InvoiceModal from "../components/invoices/InvoiceModal";
import CsvImportModal from "../components/invoices/CsvImportModal";
import HelpTooltip from "../components/common/HelpTooltip";
import UpgradePrompt from "../components/common/UpgradePrompt";
import { useAppState, DEFAULT_DUE_DAYS, addDaysToDate } from "../hooks/useAppState";
import { useSubscription } from "../hooks/useSubscription";
import { calcolaNettoFattura } from "../utils/nettoCalc";
import { getAliquotaSostitutiva } from "../utils/fiscalEngine";
import { downloadCSVTemplate } from "../utils/csvImportParser";
import { exportInvoicesList } from "../services/exportService";
import { TIMESHEET_STATUS } from "../constants/timesheetStatus";

// Stili badge per tipo fattura (Standard non mostra badge).
const INVOICE_TYPE_STYLES = {
  acconto: { label: "Acconto", light: "bg-amber-50 text-amber-700", dark: "bg-amber-900/30 text-amber-300" },
  saldo: { label: "Saldo", light: "bg-blue-50 text-blue-700", dark: "bg-blue-900/30 text-blue-300" },
  parziale: { label: "Parziale", light: "bg-purple-50 text-purple-700", dark: "bg-purple-900/30 text-purple-300" },
};

// Helper per verificare se una fattura è scaduta (usa dueDate se presente)
const isInvoiceOverdue = (inv, today) => {
  if (inv.paid) return false;
  // Usa dueDate se presente, altrimenti calcola da issueDate + DEFAULT_DUE_DAYS
  const dueDate = inv.dueDate || addDaysToDate(inv.issueDate, DEFAULT_DUE_DAYS);
  return dueDate < today;
};

// Calcola i giorni di ritardo
const getDaysOverdue = (inv, today) => {
  const dueDate = inv.dueDate || addDaysToDate(inv.issueDate, DEFAULT_DUE_DAYS);
  const diffTime = new Date(today) - new Date(dueDate);
  return Math.floor(diffTime / (1000 * 60 * 60 * 24));
};

function parseToNumber(value) {
  if (typeof value === "number") return value;
  if (value === null || value === undefined) return NaN;
  const s = String(value).trim().replace(/\s/g, "").replace(/\./g, "").replace(",", ".");
  return Number(s);
}

function formatCurrency(value) {
  const n = parseToNumber(value);
  if (!Number.isFinite(n)) return "—";
  const sign = n < 0 ? "-" : "";
  const abs = Math.abs(n);
  const [intPart, decPart] = abs.toFixed(2).split(".");
  const groupedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${sign}${groupedInt},${decPart} €`;
}

function formatDateIT(isoDate) {
  if (!isoDate) return "—";
  const [year, month, day] = isoDate.split("-");
  if (!year || !month || !day) return isoDate;
  return `${day}/${month}/${year}`;
}

export default function InvoicesPage() {
  const {
    user,
    darkMode,
    toggleDarkMode,
    selectedYear,
    setSelectedYear,
    invoices,
    clientCatalog,
    nextInvoiceCode,
    nextSeq,
    getInvoiceCodeForYear,
    createInvoice,
    duplicateInvoice,
    updateInvoice,
    deleteInvoice,
    updateTimesheet,
    importInvoicesBulk,
    fiscalSettings,
    isSynced,
    createClient,
    clients,
  } = useAppState();

  const { loading: subLoading, getInvoiceLimit, isPro } = useSubscription();
  const [showExportUpgrade, setShowExportUpgrade] = useState(false);

  // Aliquota per il "Netto" mostrato in tabella: dell'anno selezionato, lo stesso
  // che calcolaNettoFattura riceve per l'aliquota INPS.
  const aliquotaAutomatica = getAliquotaSostitutiva(fiscalSettings.pivaYearStart, selectedYear);

  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const today = new Date().toISOString().split("T")[0];

  // Filtri
  const [statusFilter, setStatusFilter] = useState(() => {
    return searchParams.get("filter") || "all";
  });
  const [clientFilter, setClientFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [editing, setEditing] = useState(null);
  const [pendingTimesheetId, setPendingTimesheetId] = useState(null);
  const [csvOpen, setCsvOpen] = useState(false);
  const [csvDropdownOpen, setCsvDropdownOpen] = useState(false);

  // Selection state
  const [selectedIds, setSelectedIds] = useState([]);

  // Reset selection when year changes
  useEffect(() => {
    setSelectedIds([]);
  }, [selectedYear]);

  // Se arriviamo da Timesheet con una fattura precompilata (via router state),
  // apriamo il modale in modalità "duplicate" (precompila senza id, per revisione).
  // Ripuliamo subito lo state di navigazione così un refresh/back non riapre il modale.
  useEffect(() => {
    if (location.state?.invoicePrefill) {
      setEditing(location.state.invoicePrefill);
      setModalMode("duplicate");
      setModalOpen(true);
      if (location.state.timesheetId) setPendingTimesheetId(location.state.timesheetId);
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // Sync URL params with filter (only when URL changes externally)
  useEffect(() => {
    const urlFilter = searchParams.get("filter");
    if (urlFilter && urlFilter !== statusFilter) {
      setStatusFilter(urlFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Fatture filtrate per anno (emissione)
  const invoicesForYear = useMemo(() => {
    return invoices.filter((inv) => {
      if (!inv.issueDate) return false;
      const issueYear = new Date(inv.issueDate).getFullYear();
      return issueYear === selectedYear;
    });
  }, [invoices, selectedYear]);

  // Clienti unici per l'anno selezionato
  const clientsForYear = useMemo(() => {
    const set = new Set();
    invoicesForYear.forEach((inv) => {
      if (inv.clientName) set.add(inv.clientName);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, "it"));
  }, [invoicesForYear]);

  // Fatture filtrate con tutti i criteri
  const filteredInvoices = useMemo(() => {
    return invoicesForYear.filter((inv) => {
      // Filtro stato
      if (statusFilter === "paid" && !inv.paid) return false;
      if (statusFilter === "unpaid" && inv.paid) return false;
      if (statusFilter === "overdue") {
        if (!isInvoiceOverdue(inv, today)) return false;
      }

      // Filtro cliente
      if (clientFilter !== "all" && inv.clientName !== clientFilter) return false;

      // Ricerca testuale
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase().trim();
        const matchCode = (inv.invoiceCode || "").toLowerCase().includes(query);
        const matchClient = (inv.clientName || "").toLowerCase().includes(query);
        const matchNotes = (inv.notes || "").toLowerCase().includes(query);
        if (!matchCode && !matchClient && !matchNotes) return false;
      }

      return true;
    });
  }, [invoicesForYear, statusFilter, clientFilter, searchQuery, today]);

  // Ordinati per data decrescente
  const sortedInvoices = useMemo(() => {
    return [...filteredInvoices].sort((a, b) => {
      return new Date(b.issueDate) - new Date(a.issueDate);
    });
  }, [filteredInvoices]);

  // Riepilogo totali
  const summary = useMemo(() => {
    // Fatturato = fatture EMESSE nell'anno selezionato (principio di competenza).
    const totalEmesso = invoicesForYear.reduce((sum, inv) => sum + (parseToNumber(inv.grossAmount) || 0), 0);
    // Incassato = fatture INCASSATE nell'anno selezionato (principio di cassa):
    // paid + paymentDate nell'anno. Include fatture emesse in anni precedenti ma
    // incassate quest'anno; coerente con incassiAnno in DashboardPage.
    const totalIncassato = invoices
      .filter((inv) => {
        if (!inv.paid) return false;
        const dateToUse = inv.paymentDate || inv.issueDate;
        return dateToUse && new Date(dateToUse).getFullYear() === selectedYear;
      })
      .reduce((sum, inv) => sum + (parseToNumber(inv.grossAmount) || 0), 0);
    // Da incassare = fatture EMESSE nell'anno ancora non pagate.
    const daIncassare = invoicesForYear
      .filter((inv) => !inv.paid)
      .reduce((sum, inv) => sum + (parseToNumber(inv.grossAmount) || 0), 0);
    const overdueCount = invoicesForYear.filter((inv) => isInvoiceOverdue(inv, today)).length;
    return { totalEmesso, totalIncassato, daIncassare, overdueCount };
  }, [invoices, invoicesForYear, selectedYear, today]);

  // Conteggio fatture del mese corrente (per il limite del piano Free)
  const invoicesThisMonthCount = useMemo(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    return invoices.filter((inv) => {
      if (!inv.issueDate) return false;
      const d = new Date(inv.issueDate);
      return d.getFullYear() === y && d.getMonth() === m;
    }).length;
  }, [invoices]);

  const invoiceLimit = getInvoiceLimit();
  const invoiceLimitReached = !subLoading && Number.isFinite(invoiceLimit) && invoicesThisMonthCount >= invoiceLimit;

  // Handlers
  const openCreate = () => {
    setEditing(null);
    setModalMode("create");
    setModalOpen(true);
  };

  const openEdit = (inv) => {
    setEditing(inv);
    setModalMode("edit");
    setModalOpen(true);
  };

  const openDuplicate = (inv) => {
    setEditing(inv);
    setModalMode("duplicate");
    setModalOpen(true);
  };

  const openAnnulla = (inv) => {
    setEditing({
      ...inv,
      grossAmount: -Math.abs(Number(inv.grossAmount) || 0),
      notes: `Nota di credito a storno fattura n. ${inv.invoiceCode || ""}`.trim(),
      paid: false,        // la nota di credito nasce non incassata
      paymentDate: "",
    });
    setModalMode("annulla");
    setModalOpen(true);
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const allSelected = sortedInvoices.length > 0 && sortedInvoices.every((i) => selectedIds.includes(i.id));

  const toggleSelectAll = () => {
    if (allSelected) setSelectedIds([]);
    else setSelectedIds(sortedInvoices.map((i) => i.id));
  };

  const deleteSelected = () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Eliminare ${selectedIds.length} fatture selezionate?`)) return;
    selectedIds.forEach((id) => deleteInvoice(id));
    setSelectedIds([]);
  };

  const clearFilters = () => {
    setStatusFilter("all");
    setClientFilter("all");
    setSearchQuery("");
    setSearchParams({});
  };

  const hasActiveFilters = statusFilter !== "all" || clientFilter !== "all" || searchQuery.trim() !== "";

  const handleExportInvoices = () => {
    if (!isPro) {
      setShowExportUpgrade(true);
      return;
    }
    exportInvoicesList(selectedYear, invoices);
  };

  // Available years
  const years = [selectedYear - 2, selectedYear - 1, selectedYear, selectedYear + 1];

  return (
    <div className={`min-h-screen transition-colors ${darkMode ? 'bg-neutral-950 text-white' : 'bg-neutral-50 text-neutral-900'}`}>
      <TopBar
        user={user}
        notificationsCount={0}
        darkMode={darkMode}
        toggleDarkMode={toggleDarkMode}
        onUploadLogo={() => {}}
        isSynced={isSynced}
      />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 pb-24">
        {/* Header */}
        <div className="mt-6 mb-6">
          <h1 className={`text-xl font-semibold flex items-center gap-2 ${darkMode ? 'text-white' : 'text-neutral-900'}`}>
            Fatture
            <HelpTooltip text="Qui trovi tutte le fatture emesse. Usa i filtri per cercare per cliente, stato o periodo." darkMode={darkMode} />
          </h1>
          <p className={`mt-1 text-sm ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>
            Gestisci le tue fatture, importa da CSV o crea nuove fatture.
          </p>
        </div>

        {/* Barra filtri e azioni */}
        <div className={`rounded-xl border p-4 mb-4 ${darkMode ? 'bg-neutral-900 border-neutral-800' : 'bg-white border-neutral-200'}`}>
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            {/* Filtri a sinistra */}
            <div className="flex flex-wrap items-center gap-2">
              {/* Anno */}
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                className={`px-3 py-2 rounded-lg border text-sm ${
                  darkMode
                    ? 'border-neutral-700 bg-neutral-800 text-white'
                    : 'border-neutral-200 bg-white text-neutral-900'
                }`}
              >
                {years.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>

              {/* Stato */}
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  if (e.target.value === "all") {
                    searchParams.delete("filter");
                  } else {
                    searchParams.set("filter", e.target.value);
                  }
                  setSearchParams(searchParams);
                }}
                className={`px-3 py-2 rounded-lg border text-sm ${
                  darkMode
                    ? 'border-neutral-700 bg-neutral-800 text-white'
                    : 'border-neutral-200 bg-white text-neutral-900'
                }`}
              >
                <option value="all">Tutte</option>
                <option value="paid">Incassate</option>
                <option value="unpaid">Da incassare</option>
                <option value="overdue">Scadute</option>
              </select>

              {/* Cliente */}
              <select
                value={clientFilter}
                onChange={(e) => setClientFilter(e.target.value)}
                className={`px-3 py-2 rounded-lg border text-sm max-w-[180px] ${
                  darkMode
                    ? 'border-neutral-700 bg-neutral-800 text-white'
                    : 'border-neutral-200 bg-white text-neutral-900'
                }`}
              >
                <option value="all">Tutti i clienti</option>
                {clientsForYear.map((client) => (
                  <option key={client} value={client}>{client}</option>
                ))}
              </select>

              {/* Ricerca */}
              <div className="relative">
                <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${darkMode ? 'text-neutral-500' : 'text-neutral-400'}`} />
                <input
                  type="text"
                  placeholder="Cerca..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={`pl-9 pr-3 py-2 rounded-lg border text-sm w-40 ${
                    darkMode
                      ? 'border-neutral-700 bg-neutral-800 text-white placeholder-neutral-400'
                      : 'border-neutral-200 bg-white text-neutral-900 placeholder-neutral-400'
                  }`}
                />
              </div>

              {/* Clear filters */}
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm ${
                    darkMode
                      ? 'text-neutral-400 hover:bg-neutral-800'
                      : 'text-neutral-600 hover:bg-neutral-100'
                  }`}
                >
                  <X className="w-4 h-4" />
                  Reset
                </button>
              )}
            </div>

            {/* Azioni a destra */}
            <div className="flex items-center gap-2">
              {/* CSV Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setCsvDropdownOpen(!csvDropdownOpen)}
                  className={`flex items-center gap-1 px-3 py-2 rounded-lg border text-sm ${
                    darkMode
                      ? 'border-neutral-700 bg-neutral-800 text-neutral-300 hover:bg-neutral-700'
                      : 'border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50'
                  }`}
                >
                  CSV
                  <ChevronDown className="w-4 h-4" />
                </button>
                {csvDropdownOpen && (
                  <div className={`absolute right-0 mt-1 w-48 rounded-lg border shadow-lg py-1 z-20 ${
                    darkMode ? 'bg-neutral-900 border-neutral-700' : 'bg-white border-neutral-200'
                  }`}>
                    <button
                      onClick={() => {
                        downloadCSVTemplate();
                        setCsvDropdownOpen(false);
                      }}
                      className={`w-full px-4 py-2 text-sm text-left flex items-center gap-2 ${
                        darkMode ? 'text-neutral-300 hover:bg-neutral-800' : 'text-neutral-700 hover:bg-neutral-50'
                      }`}
                    >
                      <Download className="w-4 h-4" />
                      Scarica template
                    </button>
                    <button
                      onClick={() => {
                        setCsvOpen(true);
                        setCsvDropdownOpen(false);
                      }}
                      className={`w-full px-4 py-2 text-sm text-left flex items-center gap-2 ${
                        darkMode ? 'text-neutral-300 hover:bg-neutral-800' : 'text-neutral-700 hover:bg-neutral-50'
                      }`}
                    >
                      <Upload className="w-4 h-4" />
                      Importa CSV
                    </button>
                  </div>
                )}
              </div>

              {/* Esporta elenco */}
              <button
                onClick={handleExportInvoices}
                className={`flex items-center gap-1 px-3 py-2 rounded-lg border text-sm ${
                  darkMode
                    ? 'border-neutral-700 bg-neutral-800 text-neutral-300 hover:bg-neutral-700'
                    : 'border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50'
                }`}
              >
                <FileDown className="w-4 h-4" />
                Esporta Elenco
              </button>

              {/* Delete selected */}
              {selectedIds.length > 0 && (
                <button
                  onClick={deleteSelected}
                  className="px-3 py-2 rounded-lg bg-red-600 text-white text-sm hover:bg-red-700"
                >
                  Elimina ({selectedIds.length})
                </button>
              )}

              {/* New invoice */}
              <div className="relative group">
                <button
                  onClick={openCreate}
                  disabled={invoiceLimitReached}
                  title={invoiceLimitReached ? "Limite fatture del piano Free raggiunto — Passa a Pro" : undefined}
                  className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm transition-colors ${
                    invoiceLimitReached
                      ? "bg-forfy-200 text-white/80 cursor-not-allowed opacity-60"
                      : "bg-forfy-500 text-white hover:bg-forfy-600"
                  }`}
                >
                  <Plus className="w-4 h-4" />
                  Nuova fattura
                </button>
                {invoiceLimitReached && (
                  <div
                    className={`absolute right-0 top-full mt-1 w-56 rounded-lg border px-3 py-2 text-xs shadow-lg opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity z-20 ${
                      darkMode
                        ? "bg-neutral-900 border-neutral-700 text-neutral-300"
                        : "bg-white border-neutral-200 text-neutral-600"
                    }`}
                  >
                    Limite fatture del piano Free raggiunto — Passa a Pro
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Riepilogo rapido */}
        <div className={`rounded-xl border p-4 mb-4 ${darkMode ? 'bg-neutral-900 border-neutral-800' : 'bg-white border-neutral-200'}`}>
          <div className="flex flex-wrap items-center gap-6">
            <div>
              <div className={`text-xs ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>Fatturato {selectedYear}</div>
              <div className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-neutral-900'}`}>
                {formatCurrency(summary.totalEmesso)}
              </div>
            </div>
            <div className={`hidden sm:block h-8 w-px ${darkMode ? 'bg-neutral-800' : 'bg-neutral-200'}`} />
            <div>
              <div className={`text-xs ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>Incassato {selectedYear}</div>
              <div className={`text-lg font-semibold ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                {formatCurrency(summary.totalIncassato)}
              </div>
            </div>
            <div className={`hidden sm:block h-8 w-px ${darkMode ? 'bg-neutral-800' : 'bg-neutral-200'}`} />
            <div>
              <div className={`text-xs ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>Da incassare</div>
              <div className={`text-lg font-semibold ${
                summary.daIncassare > 0
                  ? darkMode ? 'text-amber-400' : 'text-amber-600'
                  : darkMode ? 'text-white' : 'text-neutral-900'
              }`}>
                {formatCurrency(summary.daIncassare)}
              </div>
            </div>
            {summary.overdueCount > 0 && (
              <>
                <div className={`hidden sm:block h-8 w-px ${darkMode ? 'bg-neutral-800' : 'bg-neutral-200'}`} />
                <div>
                  <div className={`text-xs ${darkMode ? 'text-red-400' : 'text-red-600'}`}>Scadute</div>
                  <div className={`text-lg font-semibold ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
                    {summary.overdueCount}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Tabella fatture */}
        <div className={`rounded-xl border overflow-hidden ${darkMode ? 'border-neutral-800 bg-neutral-900' : 'border-neutral-200 bg-white'}`}>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className={`text-xs ${darkMode ? 'bg-neutral-800 text-neutral-400' : 'bg-neutral-50 text-neutral-600'}`}>
                <tr>
                  <th className="px-4 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      className="rounded"
                    />
                  </th>
                  <th className="text-left px-4 py-3">Data</th>
                  <th className="text-left px-4 py-3 hidden lg:table-cell">Scadenza</th>
                  <th className="text-left px-4 py-3">Numero</th>
                  <th className="text-left px-4 py-3">Cliente</th>
                  <th className="text-right px-4 py-3">
                    <span className="inline-flex items-center gap-1">
                      Importo
                      <HelpTooltip text="L'importo totale fatturato al cliente, al lordo di tasse e contributi." darkMode={darkMode} />
                    </span>
                  </th>
                  <th className="text-center px-4 py-3">Stato</th>
                  <th className="text-left px-4 py-3 hidden md:table-cell">Incasso</th>
                  <th className="text-right px-4 py-3">Azioni</th>
                </tr>
              </thead>

              <tbody className={darkMode ? 'text-white' : 'text-neutral-900'}>
                {sortedInvoices.length === 0 ? (
                  <tr className={`border-t ${darkMode ? 'border-neutral-800' : 'border-neutral-200'}`}>
                    <td className={`px-4 py-8 text-center ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`} colSpan={9}>
                      {hasActiveFilters
                        ? "Nessuna fattura corrisponde ai filtri selezionati."
                        : "Nessuna fattura per questo anno."}
                    </td>
                  </tr>
                ) : (
                  sortedInvoices.map((inv) => {
                    const isOverdue = isInvoiceOverdue(inv, today);
                    const daysOverdue = isOverdue ? getDaysOverdue(inv, today) : 0;
                    const dueDate = inv.dueDate || addDaysToDate(inv.issueDate, DEFAULT_DUE_DAYS);

                    return (
                      <tr
                        key={inv.id}
                        className={`border-t ${darkMode ? 'border-neutral-800' : 'border-neutral-200'} ${
                          selectedIds.includes(inv.id)
                            ? darkMode ? 'bg-forfy-900/30' : 'bg-forfy-50'
                            : ''
                        }`}
                      >
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(inv.id)}
                            onChange={() => toggleSelect(inv.id)}
                            className="rounded"
                          />
                        </td>

                        <td className="px-4 py-3 whitespace-nowrap">{formatDateIT(inv.issueDate)}</td>
                        <td className={`px-4 py-3 whitespace-nowrap hidden lg:table-cell ${
                          isOverdue
                            ? darkMode ? 'text-red-400' : 'text-red-600'
                            : ''
                        }`}>
                          {formatDateIT(dueDate)}
                          {isOverdue && (
                            <span className="ml-1 text-xs">({daysOverdue}gg)</span>
                          )}
                        </td>
                        <td className="px-4 py-3">{inv.invoiceCode || "-"}</td>
                        <td className="px-4 py-3 max-w-[220px]">
                          <div className="truncate">{inv.clientName || "-"}</div>
                          {inv.invoiceType && inv.invoiceType !== "standard" && INVOICE_TYPE_STYLES[inv.invoiceType] && (
                            <span className={`inline-block mt-0.5 text-[10px] px-1.5 py-0.5 rounded-full ${darkMode ? INVOICE_TYPE_STYLES[inv.invoiceType].dark : INVOICE_TYPE_STYLES[inv.invoiceType].light}`}>
                              {INVOICE_TYPE_STYLES[inv.invoiceType].label}
                            </span>
                          )}
                          {inv.parentInvoiceId && (() => {
                            const linked = invoices.find((i) => i.id === inv.parentInvoiceId);
                            return linked ? (
                              <div className="text-[10px] text-neutral-500">↳ Acconto: {linked.invoiceCode || "—"}</div>
                            ) : null;
                          })()}
                          {inv.quoteId && (
                            <div className="text-[10px] text-neutral-500">↳ da preventivo</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap font-medium">
                          {formatCurrency(inv.grossAmount)}
                          {parseToNumber(inv.grossAmount) > 0 && (() => {
                            const { netto, percentuale } = calcolaNettoFattura(
                              parseToNumber(inv.grossAmount),
                              { profitabilityCoefficient: fiscalSettings.profitabilityCoefficient, taxRate: aliquotaAutomatica, inpsType: fiscalSettings.inpsType },
                              selectedYear
                            );
                            return (
                              <div className={`text-xs mt-0.5 ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                                Netto: {formatCurrency(netto)} ({Math.round(percentuale)}%)
                              </div>
                            );
                          })()}
                        </td>

                        <td className="px-4 py-3 text-center">
                          <button
                            className={`text-xs px-2 py-1 rounded-lg border ${
                              inv.paid
                                ? darkMode
                                  ? 'border-emerald-700 bg-emerald-900/30 text-emerald-400'
                                  : 'border-emerald-200 bg-emerald-50 text-emerald-700'
                                : isOverdue
                                  ? darkMode
                                    ? 'border-red-700 bg-red-900/30 text-red-400'
                                    : 'border-red-200 bg-red-50 text-red-700'
                                  : darkMode
                                    ? 'border-neutral-700 bg-neutral-800 text-neutral-300'
                                    : 'border-neutral-200 bg-white text-neutral-700'
                            }`}
                            onClick={() =>
                              updateInvoice(inv.id, {
                                paid: !inv.paid,
                                paymentDate: !inv.paid ? today : "",
                              })
                            }
                          >
                            {inv.paid ? "✓ Sì" : isOverdue ? "Scaduta" : "No"}
                          </button>
                        </td>

                        <td className="px-4 py-3 hidden md:table-cell whitespace-nowrap">
                          {inv.paid ? formatDateIT(inv.paymentDate) : "—"}
                        </td>

                        <td className="px-4 py-3 text-right">
                          <div className="inline-flex items-center gap-2">
                            <button
                              className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded hover:underline ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}
                              onClick={() => openAnnulla(inv)}
                              title="Crea nota di credito a storno"
                            >
                              <FileX size={13} /> Nota di credito
                            </button>
                            <button
                              className={`text-xs px-2 py-1 rounded hover:underline ${darkMode ? 'text-neutral-400' : 'text-neutral-600'}`}
                              onClick={() => openDuplicate(inv)}
                            >
                              Duplica
                            </button>
                            <button
                              className={`text-xs px-2 py-1 rounded hover:underline ${darkMode ? 'text-neutral-400' : 'text-neutral-600'}`}
                              onClick={() => openEdit(inv)}
                            >
                              Modifica
                            </button>
                            <button
                              className={`text-xs px-2 py-1 rounded hover:underline ${darkMode ? 'text-red-400' : 'text-red-600'}`}
                              onClick={() => {
                                if (window.confirm("Eliminare questa fattura?")) deleteInvoice(inv.id);
                              }}
                            >
                              Elimina
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer nota */}
        <div className={`mt-3 text-xs ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>
          {sortedInvoices.length > 0 && (
            <span>
              Mostrando {sortedInvoices.length} di {invoicesForYear.length} fatture
              {hasActiveFilters && " (filtrate)"}
              {" · "}
            </span>
          )}
          Export PDF singola fattura — coming soon
        </div>
      </main>

      {/* Modals */}
      <InvoiceModal
        open={modalOpen}
        mode={modalMode}
        initialValues={editing}
        nextInvoiceCode={nextInvoiceCode}
        nextSeq={nextSeq}
        getInvoiceCodeForYear={getInvoiceCodeForYear}
        year={selectedYear}
        clientCatalog={clientCatalog}
        onClose={() => setModalOpen(false)}
        darkMode={darkMode}
        onSubmit={async (values) => {
          if (modalMode === "edit" && editing) {
            updateInvoice(editing.id, values);
          } else if (pendingTimesheetId) {
            // Fattura originata da un timesheet: crea, collega e marca il timesheet come Fatturato.
            const newId = await createInvoice(values);
            if (newId) {
              await updateTimesheet(pendingTimesheetId, { status: TIMESHEET_STATUS.INVOICED, invoiceId: newId });
            }
            setPendingTimesheetId(null);
          } else if (modalMode === "duplicate") {
            duplicateInvoice(values);
          } else {
            createInvoice(values);
          }
          setModalOpen(false);
        }}
      />

      <CsvImportModal
        open={csvOpen}
        onClose={() => setCsvOpen(false)}
        darkMode={darkMode}
        existingClients={clients}
        onConfirm={async (rows, options = {}) => {
          // Map clientName (lowercase) → clientId per i clienti già esistenti
          const clientIdByName = new Map();
          (clients || []).forEach((c) => {
            if (c?.name) clientIdByName.set(c.name.toLowerCase(), c.id);
          });

          // Crea clienti mancanti se richiesto e arricchisce la map con i nuovi ID
          let createdCount = 0;
          if (options.createClients && options.newClients?.length > 0) {
            for (const nc of options.newClients) {
              const newId = await createClient({
                name: nc.name,
                vatNumber: nc.vatNumber || "",
                email: nc.email || "",
                pecEmail: nc.pecEmail || "",
                phone: nc.phone || "",
                contactPerson: nc.contactPerson || "",
                source: "csv_import",
              });
              if (newId && nc.name) {
                clientIdByName.set(nc.name.toLowerCase(), newId);
                createdCount++;
              }
            }
          }

          // Collega ogni fattura al clientId risolto (se disponibile)
          const rowsWithClientId = rows.map((r) => {
            const key = (r.clientName || "").toLowerCase();
            const clientId = clientIdByName.get(key);
            return clientId ? { ...r, clientId } : r;
          });

          const result = await importInvoicesBulk(rowsWithClientId);
          const parts = [`Importate ${result?.imported ?? 0} fatture`];
          if (createdCount > 0) parts.push(`creati ${createdCount} nuovi clienti`);
          if (result?.skipped > 0) parts.push(`${result.skipped} duplicate saltate`);
          alert(parts.join(", ") + ".");
          setCsvOpen(false);
        }}
      />

      {/* Click outside to close CSV dropdown */}
      {csvDropdownOpen && (
        <div
          className="fixed inset-0 z-10"
          onClick={() => setCsvDropdownOpen(false)}
        />
      )}

      {/* Upgrade prompt per export (utente non Pro che prova a esportare) */}
      {showExportUpgrade && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setShowExportUpgrade(false)}>
          <div onClick={(e) => e.stopPropagation()}>
            <UpgradePrompt
              title="Export PDF disponibile con Pro"
              message="Esporta l'elenco fatture in PDF passando al piano Pro."
            />
          </div>
        </div>
      )}
    </div>
  );
}
